import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Row, Col, Card, Typography, Spin, Button, Tag } from "antd";
import {
  GlobalOutlined, EnvironmentOutlined, HomeOutlined, TeamOutlined, UserOutlined,
  ApartmentOutlined, BankOutlined, PlusOutlined, ArrowRightOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../config";

const { Title, Text } = Typography;

const STAT_CARDS = [
  { key: "countries",  title: "Countries",  route: "/pop-country",       icon: GlobalOutlined,      color: "#2563eb", bg: "#eff6ff" },
  { key: "states",     title: "States",     route: "/pop-states",        icon: EnvironmentOutlined, color: "#059669", bg: "#f0fdf4" },
  { key: "districts",  title: "Districts",  route: "/pop-districts",     icon: ApartmentOutlined,   color: "#7c3aed", bg: "#f5f3ff" },
  { key: "tehsils",    title: "Tehsils",    route: "/pop-tehsils",       icon: EnvironmentOutlined, color: "#d97706", bg: "#fffbeb" },
  { key: "panchayats", title: "Panchayats", route: "/pop-panchayat",     icon: BankOutlined,        color: "#0891b2", bg: "#ecfeff" },
  { key: "villages",   title: "Villages",   route: "/pop-village",       icon: HomeOutlined,        color: "#16a34a", bg: "#f0fdf4" },
  { key: "families",   title: "Families",   route: "/pop-families",      icon: TeamOutlined,        color: "#dc2626", bg: "#fef2f2" },
  { key: "members",    title: "Members",    route: "/pop-family-member", icon: UserOutlined,        color: "#1d4ed8", bg: "#eff6ff" },
];

const HIERARCHY = [
  { label: "Country",   route: "/pop-country",         color: "#2563eb" },
  { label: "State",     route: "/pop-states",          color: "#059669" },
  { label: "District",  route: "/pop-districts",       color: "#7c3aed" },
  { label: "Tehsil",    route: "/pop-tehsils",         color: "#d97706" },
  { label: "Panchayat", route: "/pop-panchayat",       color: "#0891b2" },
  { label: "Village",   route: "/pop-village",         color: "#16a34a" },
  { label: "Family",    route: "/pop-families",        color: "#dc2626" },
  { label: "Member",    route: "/pop-family-member",   color: "#1d4ed8" },
];

const QUICK_ACTIONS = [
  { label: "Add Country",  route: "/pop-country",         color: "#2563eb" },
  { label: "Add State",    route: "/pop-states",          color: "#059669" },
  { label: "Add District", route: "/pop-districts",       color: "#7c3aed" },
  { label: "Add Village",  route: "/pop-village",         color: "#16a34a" },
  { label: "Add Family",   route: "/pop-families/add",    color: "#dc2626" },
  { label: "Add Member",   route: "/pop-family-member/add", color: "#1d4ed8" },
];

function PopDashboard() {
  const history = useHistory();
  const authHeader = { Authorization: `Bearer ${localStorage.getItem("accessToken")}` };
  const [stats, setStats] = useState({ countries: 0, states: 0, districts: 0, tehsils: 0, panchayats: 0, villages: 0, families: 0, members: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = { page: 1, limit: 1 };
    Promise.all([
      axios.get(`${baseUrl}/pop-country`,       { headers: authHeader, params }),
      axios.get(`${baseUrl}/pop-states`,        { headers: authHeader, params }),
      axios.get(`${baseUrl}/pop-districts`,     { headers: authHeader, params }),
      axios.get(`${baseUrl}/pop-tehsils`,       { headers: authHeader, params }),
      axios.get(`${baseUrl}/pop-panchayat`,     { headers: authHeader, params }),
      axios.get(`${baseUrl}/pop-village`,       { headers: authHeader, params }),
      axios.get(`${baseUrl}/pop-families`,      { headers: authHeader, params }),
      axios.get(`${baseUrl}/pop-family-member`, { headers: authHeader, params }),
    ]).then(([c, s, d, t, p, v, f, m]) => {
      setStats({
        countries:  c.data.totalCount || 0,
        states:     s.data.totalCount || 0,
        districts:  d.data.totalCount || 0,
        tehsils:    t.data.totalCount || 0,
        panchayats: p.data.totalCount || 0,
        villages:   v.data.totalCount || 0,
        families:   f.data.totalCount || 0,
        members:    m.data.totalCount || 0,
      });
    }).catch(console.error).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="tabled">
      {/* ── Header ── */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0, color: '#1e293b', fontWeight: 800 }}>Population Dashboard</Title>
          <Text style={{ color: '#64748b', fontSize: 14 }}>Manage and monitor community population data</Text>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button icon={<PlusOutlined />} onClick={() => history.push('/pop-families/add')}
            style={{ background: '#dc2626', borderColor: '#dc2626', color: '#fff', fontWeight: 600, borderRadius: 8 }}>
            Add Family
          </Button>
          <Button icon={<PlusOutlined />} onClick={() => history.push('/pop-family-member/add')}
            style={{ background: '#1d4ed8', borderColor: '#1d4ed8', color: '#fff', fontWeight: 600, borderRadius: 8 }}>
            Add Member
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "80px 0" }}><Spin size="large" /></div>
      ) : (
        <>
          {/* ── Stat Cards ── */}
          <Row gutter={[16, 16]}>
            {STAT_CARDS.map(({ key, title, route, icon: Icon, color, bg }) => (
              <Col key={key} xs={12} sm={8} md={6}>
                <Card
                  hoverable
                  onClick={() => history.push(route)}
                  style={{ borderRadius: 12, border: `1px solid ${color}25`, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", cursor: "pointer" }}
                  bodyStyle={{ padding: "18px 20px" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ background: bg, borderRadius: 10, width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon style={{ fontSize: 22, color }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 28, fontWeight: 800, color: '#1e293b', lineHeight: 1.1, letterSpacing: '-0.5px' }}>
                        {stats[key].toLocaleString()}
                      </div>
                      <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginTop: 2 }}>{title}</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 14, borderTop: `1px solid ${color}15`, paddingTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                    <Text style={{ fontSize: 12, color, fontWeight: 600 }}>View all →</Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* ── Geographic Hierarchy ── */}
          <Card
            style={{ marginTop: 20, borderRadius: 12, border: '1px solid #e2e8f0' }}
            bodyStyle={{ padding: '18px 24px' }}
          >
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Text strong style={{ fontSize: 14, color: '#1e293b' }}>Geographic Hierarchy</Text>
              <Text style={{ fontSize: 12, color: '#94a3b8' }}>Click any level to manage its records</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
              {HIERARCHY.map((h, i) => (
                <React.Fragment key={h.label}>
                  <Tag
                    onClick={() => history.push(h.route)}
                    style={{
                      background: `${h.color}10`, border: `1.5px solid ${h.color}40`,
                      color: h.color, fontWeight: 700, fontSize: 13,
                      padding: '5px 16px', borderRadius: 20, cursor: 'pointer',
                      margin: 0, lineHeight: '22px',
                    }}
                  >
                    {h.label}
                  </Tag>
                  {i < HIERARCHY.length - 1 && (
                    <ArrowRightOutlined style={{ color: '#cbd5e1', fontSize: 13 }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </Card>

          {/* ── Quick Actions ── */}
          <Card
            style={{ marginTop: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}
            bodyStyle={{ padding: '18px 24px' }}
          >
            <Text strong style={{ fontSize: 14, color: '#1e293b', display: 'block', marginBottom: 14 }}>Quick Actions</Text>
            <Row gutter={[10, 10]}>
              {QUICK_ACTIONS.map(({ label, route, color }) => (
                <Col key={label} xs={12} sm={8} md={4}>
                  <Button
                    block
                    icon={<PlusOutlined />}
                    onClick={() => history.push(route)}
                    style={{ background: `${color}0d`, borderColor: `${color}50`, color, fontWeight: 600, borderRadius: 8, height: 38 }}
                  >
                    {label}
                  </Button>
                </Col>
              ))}
            </Row>
          </Card>
        </>
      )}
    </div>
  );
}

export default PopDashboard;
